<template>
    <section class="mt-5 border-t border-foxy-border pt-4">
        <fieldset
            ref="formatFieldset"
            tabindex="-1"
            class="min-w-0 outline-none"
            :aria-describedby="fieldsetDescriptionIds"
            :aria-invalid="hasAnyError ? 'true' : 'false'"
        >
            <legend class="text-sm font-extrabold text-foxy-text">Формати читання</legend>
            <p :id="guidanceId" class="mt-2 text-xs text-foxy-text-secondary">Можна вибрати щонайбільше чотири формати.</p>

            <p v-if="!activeRows.length" class="mt-3 rounded-xl border border-dashed border-foxy-border p-3 text-sm text-foxy-text-secondary">
                Немає форматів, доступних для нового вибору.
            </p>

            <div v-if="activeRows.length" class="mt-3">
                <div v-if="activeOrdinaryRows.length" class="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    <div
                        v-for="row in activeOrdinaryRows"
                        :key="identity(row.code)"
                        class="min-w-0 rounded-xl border border-foxy-border bg-foxy-surface/45 p-2.5 lg:px-2 lg:py-0"
                    >
                        <label
                            :for="checkboxId(row)"
                            class="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-semibold text-foxy-text"
                            :class="isChoiceDisabled(row) ? 'cursor-not-allowed opacity-60' : ''"
                        >
                            <input
                                :id="checkboxId(row)"
                                :ref="`checkbox-${identity(row.code)}`"
                                type="checkbox"
                                :checked="row.selected"
                                :disabled="isChoiceDisabled(row)"
                                :aria-invalid="errorFor(row, 'code') ? 'true' : 'false'"
                                :aria-describedby="checkboxDescriptionIds(row)"
                                @change="toggle(row)"
                            />
                            <span>{{ row.label }}</span>
                        </label>
                        <p v-if="errorFor(row, 'code')" :id="codeErrorId(row)" class="mt-1 text-sm text-foxy-error" role="alert">
                            {{ errorFor(row, 'code') }}
                        </p>
                    </div>
                </div>

                <div
                    v-for="row in activeOtherRows"
                    :key="identity(row.code)"
                    class="mt-2 grid min-w-0 grid-cols-1 gap-2 lg:items-start"
                    :class="row.selected ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]' : 'lg:grid-cols-3'"
                >
                    <div class="min-w-0 rounded-xl border border-foxy-border bg-foxy-surface/45 p-2.5 lg:px-2 lg:py-0">
                        <label
                            :for="checkboxId(row)"
                            class="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-semibold text-foxy-text"
                            :class="isChoiceDisabled(row) ? 'cursor-not-allowed opacity-60' : ''"
                        >
                            <input
                                :id="checkboxId(row)"
                                :ref="`checkbox-${identity(row.code)}`"
                                type="checkbox"
                                :checked="row.selected"
                                :disabled="isChoiceDisabled(row)"
                                :aria-invalid="errorFor(row, 'code') ? 'true' : 'false'"
                                :aria-describedby="checkboxDescriptionIds(row)"
                                @change="toggle(row)"
                            />
                            <span>{{ row.label }}</span>
                        </label>
                        <p v-if="errorFor(row, 'code')" :id="codeErrorId(row)" class="mt-1 text-sm text-foxy-error" role="alert">
                            {{ errorFor(row, 'code') }}
                        </p>
                    </div>

                    <div v-if="row.selected" class="min-w-0">
                        <label :for="customLabelId(row)" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary">
                            Власна назва — необов’язково
                        </label>
                        <input
                            :id="customLabelId(row)"
                            :ref="`custom-${identity(row.code)}`"
                            type="text"
                            maxlength="100"
                            class="acc-modal-input min-h-11 w-full"
                            placeholder="Наприклад, Брайль"
                            :value="row.customLabel"
                            :disabled="saving"
                            :aria-invalid="errorFor(row, 'customLabel') ? 'true' : 'false'"
                            :aria-describedby="customDescriptionIds(row)"
                            @input="updateCustomLabel(row, $event.target.value)"
                        />
                        <p :id="customHelpId(row)" class="mt-1 text-xs text-foxy-text-secondary">До 100 символів.</p>
                        <p v-if="errorFor(row, 'customLabel')" :id="customErrorId(row)" class="mt-1 text-sm text-foxy-error" role="alert">
                            {{ errorFor(row, 'customLabel') }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="historicalRows.length" class="mt-4">
                <p class="text-xs font-semibold text-foxy-text-secondary">Збережені формати</p>
                <div class="mt-2 space-y-2">
                    <div
                        v-for="row in historicalRows"
                        :key="identity(row.code)"
                        class="min-w-0 rounded-xl border border-foxy-border bg-foxy-surface/45 p-2.5"
                    >
                        <label
                            :for="checkboxId(row)"
                            class="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-semibold text-foxy-text"
                            :class="isChoiceDisabled(row) ? 'cursor-not-allowed opacity-60' : ''"
                        >
                            <input
                                :id="checkboxId(row)"
                                :ref="`checkbox-${identity(row.code)}`"
                                type="checkbox"
                                :checked="row.selected"
                                :disabled="isChoiceDisabled(row)"
                                :aria-invalid="errorFor(row, 'code') ? 'true' : 'false'"
                                :aria-describedby="checkboxDescriptionIds(row)"
                                @change="toggle(row)"
                            />
                            <span>{{ row.label }}</span>
                        </label>
                        <p :id="historicalNoteId(row)" class="mt-1 text-xs text-foxy-text-secondary">{{ historicalCopy(row) }}</p>
                        <p v-if="errorFor(row, 'code')" :id="codeErrorId(row)" class="mt-1 text-sm text-foxy-error" role="alert">
                            {{ errorFor(row, 'code') }}
                        </p>

                        <div v-if="isOther(row) && row.selected" class="mt-2">
                            <label :for="customLabelId(row)" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary">
                                Власна назва — необов’язково
                            </label>
                            <input
                                :id="customLabelId(row)"
                                :ref="`custom-${identity(row.code)}`"
                                type="text"
                                maxlength="100"
                                class="acc-modal-input min-h-11 w-full"
                                placeholder="Наприклад, Брайль"
                                :value="row.customLabel"
                                :disabled="saving"
                                :aria-invalid="errorFor(row, 'customLabel') ? 'true' : 'false'"
                                :aria-describedby="customDescriptionIds(row)"
                                @input="updateCustomLabel(row, $event.target.value)"
                            />
                            <p :id="customHelpId(row)" class="mt-1 text-xs text-foxy-text-secondary">До 100 символів.</p>
                            <p v-if="errorFor(row, 'customLabel')" :id="customErrorId(row)" class="mt-1 text-sm text-foxy-error" role="alert">
                                {{ errorFor(row, 'customLabel') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="groupError" :id="groupErrorId" class="mt-2 text-sm text-foxy-error" role="alert">{{ groupError }}</p>
        </fieldset>
    </section>
</template>

<script lang="ts">
function cloneRows(rows) {
    return (Array.isArray(rows) ? rows : []).map((row) => ({ ...row }));
}

export default {
    name: 'LibraryReadingFormatEditor',
    props: {
        readingId: { type: [Number, String], required: true },
        rows: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        errors: { type: Object, default: () => ({ group: '', byCode: {} }) },
    },
    computed: {
        scopeId() {
            return `reading-${this.readingId}-formats`;
        },
        guidanceId() {
            return `${this.scopeId}-guidance`;
        },
        groupErrorId() {
            return `${this.scopeId}-group-error`;
        },
        groupError() {
            return this.errors?.group || '';
        },
        activeRows() {
            return this.rows.filter((row) => row.isActiveOption);
        },
        activeOrdinaryRows() {
            return this.activeRows.filter((row) => !this.isOther(row));
        },
        activeOtherRows() {
            return this.activeRows.filter((row) => this.isOther(row));
        },
        historicalRows() {
            return this.rows.filter((row) => !row.isActiveOption && row.wasCanonicalSelection);
        },
        selectedCount() {
            return this.rows.filter((row) => row.selected).length;
        },
        hasAnyError() {
            return Boolean(this.groupError || Object.values(this.errors?.byCode || {}).some((error) => Boolean(error?.code || error?.customLabel)));
        },
        fieldsetDescriptionIds() {
            return [this.guidanceId, this.groupError ? this.groupErrorId : ''].filter(Boolean).join(' ');
        },
    },
    methods: {
        identity(code) {
            return String(code || '')
                .trim()
                .toLowerCase();
        },
        encodedCode(row) {
            return encodeURIComponent(this.identity(row.code));
        },
        checkboxId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-checkbox`;
        },
        historicalNoteId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-historical-note`;
        },
        codeErrorId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-code-error`;
        },
        customLabelId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-custom-label`;
        },
        customHelpId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-custom-help`;
        },
        customErrorId(row) {
            return `${this.scopeId}-${this.encodedCode(row)}-custom-error`;
        },
        errorFor(row, key) {
            return this.errors?.byCode?.[this.identity(row.code)]?.[key] || '';
        },
        checkboxDescriptionIds(row) {
            return [!row.isActiveOption ? this.historicalNoteId(row) : '', this.errorFor(row, 'code') ? this.codeErrorId(row) : '']
                .filter(Boolean)
                .join(' ');
        },
        customDescriptionIds(row) {
            return [this.customHelpId(row), this.errorFor(row, 'customLabel') ? this.customErrorId(row) : ''].filter(Boolean).join(' ');
        },
        isOther(row) {
            return this.identity(row.code) === 'other';
        },
        isChoiceDisabled(row) {
            return this.saving || (!row.selected && this.selectedCount >= 4);
        },
        historicalCopy(row) {
            return row.selected
                ? 'Збережено з історії — недоступний для нового вибору.'
                : 'Буде прибрано після збереження. До збереження вибір можна відновити.';
        },
        emitRows(rows) {
            this.$emit('clear-errors');
            this.$emit('update', cloneRows(rows));
        },
        toggle(row) {
            if (this.isChoiceDisabled(row)) return;
            const key = this.identity(row.code);
            this.emitRows(
                this.rows.map((candidate) => (this.identity(candidate.code) === key ? { ...candidate, selected: !candidate.selected } : candidate)),
            );
        },
        updateCustomLabel(row, value) {
            if (this.saving) return;
            const key = this.identity(row.code);
            this.emitRows(
                this.rows.map((candidate) =>
                    this.identity(candidate.code) === key ? { ...candidate, customLabel: String(value ?? '') } : candidate,
                ),
            );
        },
        focusValidation(code, field = 'code') {
            this.$nextTick(() => {
                if (!code || field === 'group') {
                    this.$refs.formatFieldset?.focus();
                    return;
                }
                const prefix = field === 'customLabel' ? 'custom' : 'checkbox';
                const reference = this.$refs[`${prefix}-${this.identity(code)}`];
                const target = Array.isArray(reference) ? reference[0] : reference;
                (target || this.$refs.formatFieldset)?.focus();
            });
        },
    },
};
</script>
